import ImageComponent from "../imgs/Image"
import '../../styles/collection.css';
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../AuthProvider";
import { addCollection, getFavouritePokemon, getMyCollection, searchCardProductsByName, searchFirstCardProductByName, updateFavouritePokemon } from "../../api/user";
import { Collection } from "../../classes/Collection";
import { getImageUrl, handleImageError } from "../../helper/image";
import DeferredImage from "../imgs/DeferredImage";
import { getProducts } from "../../api/inventory";
import { useVirtualizer } from '@tanstack/react-virtual';
import POKEMON_SPECIES from "../../data/pokemonSpecies.json";

function asImageProduct(product) {
  if (!product) return null;
  // backend shape: { id, product_name, type, set_id, ... }
  // image helper expects: { id, name, type, setId }
  return {
    id: product.id,
    name: product.product_name,
    type: product.type,
    setId: product.set_id,
  };
}

const GENERATION_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const FavouritePokemon = ({ product, onClick }) => {
  const imgProduct = asImageProduct(product);
  const highSrc = imgProduct ? getImageUrl(imgProduct, "high") : "";
  return (
    <button
      className="favourite-pokemon collectdiv-outline"
      onClick={onClick}
      type="button"
      aria-label={`Edit favourite Pokemon ${product?.product_name || ""}`}
    >
      <DeferredImage
        src={highSrc}
        alt={product?.product_name || "Favourite Pokemon"}
      />
      <div className="favourite-pokemon-label">{product?.product_name || ""}</div>
    </button>
  );
};

function titleCase(s) {
  const t = String(s || "").trim();
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function escapeRegExp(s) {
  return String(s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Match query as a whole token: start/end of string, or non-alphanumeric boundaries.
// e.g. "mew" matches "mew" / "special mew ex", but not "mewtwo".
function matchesPokemonNameToken(name, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = String(name || "").toLowerCase();
  const re = new RegExp(`(^|[^a-z0-9])${escapeRegExp(q)}([^a-z0-9]|$)`);
  return re.test(hay);
}

const FavouritePokemonEditor = ({ selectedIndex, onClose, onSelectProduct }) => {
  const [step, setStep] = useState("pokemon"); // "pokemon" → choose species, "cards" → choose product/card
  const [pokemonGenFilter, setPokemonGenFilter] = useState("all");
  const [pokemonSearch, setPokemonSearch] = useState("");

  const [chosenPokemon, setChosenPokemon] = useState(null); // { dexId, name, generation }

  const [cardsLoading, setCardsLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardImageLoaded, setCardImageLoaded] = useState({}); // product.id -> boolean
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewImageLoaded, setPreviewImageLoaded] = useState(false);
  const [previewImageFailed, setPreviewImageFailed] = useState(false);
  const [cardsListOpen, setCardsListOpen] = useState(true);
  // Lazily load a representative card image for each Pokémon row (frontend-only UX).
  const pokemonRowPreviewCacheRef = useRef(new Map()); // pokemonName -> product|null
  const pokemonRowPreviewLoadingRef = useRef(new Set()); // pokemonName
  const pokemonRowFailedIdsRef = useRef(new Map()); // pokemonName -> Set(productId)
  const [pokemonRowPreviews, setPokemonRowPreviews] = useState({}); // pokemonName -> product|null
  const [pokemonRowPreviewLoading, setPokemonRowPreviewLoading] = useState({}); // pokemonName -> boolean
  const observerRef = useRef(null);
  const pokemonScrollRef = useRef(null);
  const [pokemonColumns, setPokemonColumns] = useState(1);

  // Rough row height for virtualization. (We keep it a little higher to avoid clipping.)
  const POKEMON_ROW_HEIGHT = 250;

  const filteredPokemon = POKEMON_SPECIES
    .filter((p) => pokemonGenFilter === "all" || p.generation === Number(pokemonGenFilter))
    .filter((p) => matchesPokemonNameToken(p.name, pokemonSearch));

  useEffect(() => {
    if (step !== "pokemon") return;
    const update = () => {
      const el = pokemonScrollRef.current;
      if (!el) return;
      const GAP = 20; // matches `.pokemon-picker-scroll-list`
      const TILE_MIN = 200; // matches `.pokemon-picker-scroll-list.pokemon-picker-pokemon-list`
      const cols = Math.max(1, Math.floor((el.clientWidth + GAP) / (TILE_MIN + GAP)));
      setPokemonColumns(cols);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [step]);

  const pokemonRowCount = Math.ceil(filteredPokemon.length / pokemonColumns) || 0;
  const pokemonVirtualizer = useVirtualizer({
    count: pokemonRowCount,
    estimateSize: () => POKEMON_ROW_HEIGHT,
    getScrollElement: () => pokemonScrollRef.current,
    overscan: 2,
  });
  const pokemonVirtualItems = pokemonVirtualizer.getVirtualItems();

  useEffect(() => {
    pokemonVirtualizer.measure();
  }, [pokemonColumns, filteredPokemon.length, pokemonVirtualizer, step]);

  useEffect(() => {
    if (!chosenPokemon) return;
    if (step !== "cards") return;
    let alive = true;
    const pokemonName = chosenPokemon.name || "";

    (async () => {
      setCardsLoading(true);
      setCards([]);
      setCardImageLoaded({});
      setPreviewLoading(true);
      setPreviewProduct(null);

      const mergeCards = (existing, items) => {
        const seen = new Set(existing.map((p) => p.id));
        const next = [...existing];
        for (const p of items) {
          if (p?.type !== "CARD" || seen.has(p.id)) continue;
          seen.add(p.id);
          next.push(p);
        }
        return next;
      };

      try {
        const MAX_PAGES = 25; // safety cap so UI can't spiral on unexpected totals
        let page = 1;
        let total = Infinity;
        let acc = [];

        // Fetch first page first, so we can show a preview + initial grid quickly.
        const firstRes = await searchCardProductsByName(pokemonName, { page: 1 });
        const firstItems = (firstRes?.items || []).filter((p) => p?.type === "CARD");
        total = Number(firstRes?.total ?? firstItems.length);
        acc = mergeCards(acc, firstItems);

        if (alive) {
          setCards(acc);
          setPreviewProduct(firstItems[0] || null);
          setPreviewLoading(false);
        }

        page = 2;
        while (alive && page <= MAX_PAGES && acc.length < total) {
          const res = await searchCardProductsByName(pokemonName, { page });
          const items = (res?.items || []).filter((p) => p?.type === "CARD");
          total = Number(res?.total ?? total);
          acc = mergeCards(acc, items);

          if (alive) {
            setCards(acc);
          }

          if (!res?.items?.length) break;
          page += 1;
        }
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setCards([]);
        setPreviewProduct(null);
        setPreviewLoading(false);
      } finally {
        if (!alive) return;
        setCardsLoading(false);
        setPreviewLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [chosenPokemon, step]);

  // When the preview product changes (initial fetch or card click), reset image loading UX.
  useEffect(() => {
    setPreviewImageLoaded(false);
    setPreviewImageFailed(false);
  }, [previewProduct?.id]);

  const ensurePokemonRowPreview = useCallback(
    async (pokemonName, { force = false } = {}) => {
      const name = String(pokemonName || "").trim();
      if (!name) return;
      if (!force && pokemonRowPreviewCacheRef.current.has(name)) return;
      if (pokemonRowPreviewLoadingRef.current.has(name)) return;

      pokemonRowPreviewLoadingRef.current.add(name);
      setPokemonRowPreviewLoading((prev) => ({ ...prev, [name]: true }));

      try {
        const excludeIds = Array.from(pokemonRowFailedIdsRef.current.get(name) || []);
        const product = await searchFirstCardProductByName(name, { excludeIds });

        pokemonRowPreviewCacheRef.current.set(name, product);
        setPokemonRowPreviews((prev) => ({ ...prev, [name]: product }));
      } catch (e) {
        console.error(e);
        pokemonRowPreviewCacheRef.current.set(name, null);
        setPokemonRowPreviews((prev) => ({ ...prev, [name]: null }));
      } finally {
        pokemonRowPreviewLoadingRef.current.delete(name);
        setPokemonRowPreviewLoading((prev) => ({ ...prev, [name]: false }));
      }
    },
    []
  );

  useEffect(() => {
    if (step !== "pokemon") return;
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const pokemonName = entry.target?.getAttribute?.("data-pokemon-name");
            if (pokemonName) ensurePokemonRowPreview(pokemonName);
            observerRef.current?.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );
    }

    return () => {};
  }, [step, ensurePokemonRowPreview]);


  function handleSelectPokemon(p) {
    setChosenPokemon(p);
    setCardsListOpen(true);
    setStep("cards");
  }

  function handleBackToPokemon() {
    setStep("pokemon");
    setCards([]);
    setChosenPokemon(null);
    setCardsListOpen(true);
  }

  return (
    <div className="pokemon-picker-overlay" role="dialog" aria-modal="true">
      <div className="pokemon-picker-panel">
        <div className="pokemon-picker-header">
          <h2>
            {step === "pokemon" ? `Choose Pokemon for slot ${selectedIndex + 1}` : `Choose card for ${titleCase(chosenPokemon?.name)} (${chosenPokemon?.dexId ?? ""})`}
          </h2>
          <button type="button" onClick={onClose} className="pokemon-picker-close">
            Close
          </button>
        </div>

        {step === "pokemon" ? (
          <>
            <div className="pokemon-picker-controls">
              <div className="pokemon-generation-tabs">
                <button
                  type="button"
                  className={`pokemon-generation-tab ${pokemonGenFilter === "all" ? "active" : ""}`}
                  onClick={() => setPokemonGenFilter("all")}
                >
                  All
                </button>
                {GENERATION_NUMBERS.map((gen) => (
                  <button
                    key={gen}
                    type="button"
                    className={`pokemon-generation-tab ${pokemonGenFilter === String(gen) ? "active" : ""}`}
                    onClick={() => setPokemonGenFilter(String(gen))}
                  >
                    Gen {gen}
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={pokemonSearch}
                onChange={(e) => setPokemonSearch(e.target.value)}
                placeholder="Search pokemon name"
                className="pokemon-search-input"
              />
            </div>

            <div
              ref={pokemonScrollRef}
              className="pokemon-picker-scroll-list pokemon-picker-pokemon-list hide-scrollbar"
              // We virtualize rows, so the outer container should not be a CSS grid.
              style={{ display: "block", position: "relative" }}
            >
              {filteredPokemon.length > 0 && (
                <div style={{ position: "relative", height: pokemonVirtualizer.getTotalSize() }}>
                  {pokemonVirtualItems.map((vRow) => {
                    const rowStart = vRow.index * pokemonColumns;
                    const rowItems = filteredPokemon.slice(rowStart, rowStart + pokemonColumns);
                    return (
                      <div
                        key={vRow.key}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: POKEMON_ROW_HEIGHT,
                          transform: `translateY(${vRow.start}px)`,
                          display: "grid",
                          gridTemplateColumns: `repeat(${pokemonColumns}, 1fr)`,
                          gap: 20,
                        }}
                      >
                        {rowItems.map((p) => (
                          <button
                            key={p.dexId}
                            type="button"
                            className="pokemon-picker-pokemon-row"
                            onClick={() => handleSelectPokemon(p)}
                            data-pokemon-name={p.name}
                            ref={(el) => {
                              if (!el) return;
                              if (!observerRef.current) return;
                              observerRef.current.observe(el);
                            }}
                          >
                            <div className="pokemon-picker-pokemon-card-preview">
                              {pokemonRowPreviews?.[p.name] === undefined || pokemonRowPreviewLoading?.[p.name] ? (
                                <img
                                  className="pokemon-picker-pokemon-logo-placeholder"
                                  src="/assets/logo/collectdiv.png"
                                  alt="CollectDiv"
                                />
                              ) : pokemonRowPreviews?.[p.name] ? (
                                <img
                                  className="pokemon-picker-pokemon-card-img"
                                  src={getImageUrl(asImageProduct(pokemonRowPreviews[p.name]), "thumb")}
                                  alt={pokemonRowPreviews[p.name]?.product_name || p.name}
                                  onError={handleImageError}
                                />
                              ) : (
                                <div className="pokemon-picker-pokemon-card-loading pokemon-picker-pokemon-card-loading--empty">
                                  No card found
                                </div>
                              )}
                            </div>

                            <div className="pokemon-picker-pokemon-meta">
                              <span className="pokemon-picker-pokemon-name">{titleCase(p.name)}</span>
                              <span className="pokemon-picker-pokemon-dex">#{p.dexId}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              {filteredPokemon.length === 0 && (
                <div className="pokemon-picker-empty">No Pokémon match your filters.</div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="pokemon-picker-controls" style={{ flexDirection: "row", alignItems: "center" }}>
              <button type="button" className="pokemon-picker-close" onClick={handleBackToPokemon}>
                Back
              </button>
              <div style={{ opacity: 0.9, marginLeft: 10 }}>
                Showing cards for <b>{titleCase(chosenPokemon?.name)}</b>
              </div>
            </div>

            <div className={`pokemon-picker-preview ${cardsListOpen ? "" : "pokemon-picker-preview--enlarged"}`}>
              {previewLoading ? (
                <div className="pokemon-picker-empty">Loading preview…</div>
              ) : previewProduct ? (
                <>
                  <div className="pokemon-picker-preview-image-slot">
                    <div className="pokemon-picker-preview-pattern-wall" aria-hidden="true">
                      {(() => {
                        const cols = 4;
                        const rows = 4;
                        const count = cols * rows;
                        const densityArea = 70; // percent width/height used by logos (more gap outside)
                        const margin = (100 - densityArea) / 2;

                        return Array.from({ length: count }).map((_, idx) => {
                          const col = idx % cols;
                          const row = Math.floor(idx / cols);
                          const xOffset = (row % 2) * 0.5; // stagger alternate rows

                          const left =
                            margin + ((col + xOffset + 0.5) / cols) * densityArea;
                          const top =
                            margin + ((row + 0.5) / rows) * densityArea;

                        return (
                          <img
                            key={idx}
                            src="/assets/logo/collectdiv.png"
                            alt=""
                            style={{ left: `${left}%`, top: `${top}%` }}
                          />
                        );
                        });
                      })()}
                    </div>
                    <img
                      className={`pokemon-picker-preview-card-img ${
                        previewImageLoaded && !previewImageFailed
                          ? "pokemon-picker-preview-card-img--visible"
                          : "pokemon-picker-preview-card-img--hidden"
                      }`}
                      src={getImageUrl(asImageProduct(previewProduct), "high")}
                      alt={previewProduct.product_name}
                      onLoad={() => setPreviewImageLoaded(true)}
                      onError={handleImageError}
                    />
                  </div>
                  <div className="pokemon-picker-preview-name">{previewProduct.product_name}</div>
                </>
              ) : (
                <div className="pokemon-picker-empty">No cards found in your DB.</div>
              )}
            </div>

            <div className="pokemon-picker-confirm-container">
              <button
                type="button"
                className="pokemon-picker-confirm-button"
                disabled={!previewProduct || previewLoading}
                onClick={() => {
                  if (!previewProduct) return;
                  onSelectProduct(previewProduct);
                }}
              >
                Confirm as favourite card
              </button>
            </div>

            <button
              type="button"
              className={`pokemon-picker-cards-toggle ${cardsListOpen ? "is-open" : "is-closed"}`}
              onClick={() => setCardsListOpen((open) => !open)}
              aria-expanded={cardsListOpen}
              aria-controls="pokemon-picker-card-list"
            >
              <span className="pokemon-picker-cards-toggle-label">
                {cardsListOpen ? "Hide cards" : "Show cards"}
              </span>
              <span className="pokemon-picker-cards-toggle-arrow" aria-hidden="true">
                {cardsListOpen ? "▾" : "▴"}
              </span>
            </button>

            {cardsListOpen && (
            <div
              id="pokemon-picker-card-list"
              className="pokemon-picker-scroll-list pokemon-picker-card-list hide-scrollbar"
            >
              {cardsLoading && cards.length === 0 && (
                <div className="pokemon-picker-empty">Loading cards…</div>
              )}
              {cards.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="pokemon-picker-card-row"
                  onClick={() => {
                    setPreviewLoading(false);
                    setPreviewProduct(product);
                  }}
                >
                  <div className="pokemon-picker-card-image-slot">
                    <img
                      className={`pokemon-picker-card-logo-placeholder ${cardImageLoaded[product.id] ? "pokemon-picker-card-logo-placeholder--hidden" : ""}`}
                      src="/assets/logo/collectdiv.png"
                      alt="Loading"
                    />
                    <img
                      className={`pokemon-picker-card-img ${cardImageLoaded[product.id] ? "pokemon-picker-card-img--loaded" : ""}`}
                      src={getImageUrl(asImageProduct(product), "thumb")}
                      alt={product.product_name}
                      onLoad={() => setCardImageLoaded((prev) => ({ ...prev, [product.id]: true }))}
                      onError={handleImageError}
                    />
                  </div>
                  <span className="pokemon-picker-card-name">{product.product_name}</span>
                </button>
              ))}
              {cardsLoading && cards.length > 0 && (
                <div className="pokemon-picker-empty">Loading more cards…</div>
              )}
              {!cardsLoading && cards.length === 0 && (
                <div className="pokemon-picker-empty">No cards found in your DB for this Pokémon.</div>
              )}
            </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const PortfolioComponent = ({user}) => {
    const [favouritePokemon, setFavouritePokemon] = useState([]);
    const [editingSlot, setEditingSlot] = useState(null);
    const [savingFavourite, setSavingFavourite] = useState(false);

    useEffect(() => {
      let alive = true;
      if (!user) {
        setFavouritePokemon([]);
        return () => {
          alive = false;
        };
      }

      (async () => {
        try {
          const slots = await getFavouritePokemon();
          if (!alive) return;

          if (!Array.isArray(slots) || slots.length === 0) {
            setFavouritePokemon([]);
            return;
          }

          const sorted = [...slots].sort((a, b) => a.slot - b.slot);
          const productIds = sorted.map((item) => item.product_id);
          const products = await getProducts(productIds);
          const byId = new Map((products || []).map((p) => [p.id, p]));
          setFavouritePokemon(
            sorted.map((item) => ({
              ...item,
              product: byId.get(item.product_id) || null,
            }))
          );
        } catch (e) {
          console.error(e);
          if (alive) {
            setFavouritePokemon([]);
          }
        }
      })();

      return () => {
        alive = false;
      };
    }, [user]);

    async function persistFavouritePokemon(nextList) {
      if (!user) return;
      setSavingFavourite(true);
      try {
        const payload = nextList.map((item, slot) => ({ slot, product_id: item.product_id }));
        await updateFavouritePokemon(payload);
      } catch (e) {
        console.error(e);
      } finally {
        setSavingFavourite(false);
      }
    }

    function handleSelectProduct(product) {
      if (editingSlot === null) return;
      setFavouritePokemon((prev) => {
        const updated = [...prev];
        const nextItem = { slot: editingSlot, product_id: product.id, product };
        const existingIndex = updated.findIndex((it) => it?.product_id === product.id);

        if (existingIndex !== -1 && existingIndex !== editingSlot) {
          const previousSelected = updated[editingSlot];
          updated[editingSlot] = nextItem;
          updated[existingIndex] = previousSelected;
        } else {
          updated[editingSlot] = nextItem;
        }

        persistFavouritePokemon(updated);
        return updated;
      });
      setEditingSlot(null);
    }

    return <div id='portfolio'>
      <div className="avatar-container">
        <div id="portfolio-img">
            <img src="/avatars/avatar.png" />
        </div>
        <p>@AVGPOKECOLLECTOR</p>
        {savingFavourite && <p>Saving favourites...</p>}
        <div className="favourite-pokemon-container">
          {favouritePokemon.map((item, index) => (
            <FavouritePokemon
              key={`${index}-${item?.product_id || "empty"}`}
              product={item?.product}
              onClick={() => setEditingSlot(index)}
            />
          ))}


        </div>
      </div>
      {editingSlot !== null && (
        <FavouritePokemonEditor
          selectedIndex={editingSlot}
          onClose={() => setEditingSlot(null)}
          onSelectProduct={handleSelectProduct}
        />
      )}

    </div>
}

const CollectionDetailsComponent = ({collection}) => {
    return <div className="row">
        <div className="collection-name">{collection.collectionName}</div>
        <div className="column">
            <div className="collection-qty">{collection.quantity ?? 0} cards</div>
            <div className="collection-qty">Value: {collection.quantity ?? 0}</div>
        </div>

    </div>
}

const CardCollectionComponent = () => {
  const [collections, setCollections] = useState([]);
  const [adding, setAdding] = useState(false);

  const [collectionName, setCollectionName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

useEffect(() => {
  let alive = true;

  (async () => {
    try {
      const res = await getMyCollection();
      if (!alive) return;

      if (Array.isArray(res)) {
        const parsed = res.map(
          (c) => new Collection(c.collection_type, c.id)
        );
        setCollections(parsed);
      } else {
        setCollections([]);
      }

    } catch (e) {
      console.error(e);
      if (alive) setCollections([]);
    }
  })();

  return () => {
    alive = false;
  };
}, []);


  const resetAdd = () => {
    setAdding(false);
    setCollectionName("");
    setError("");
    setIsSaving(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;

    const name = collectionName.trim();
    if (!name) {
      setError("Please enter a collection name.");
      return;
    }

    // prevent dupes (case-insensitive)
    const exists = collections.some(
      (c) => (c.name || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      setError("That collection already exists.");
      return;
    }

    try {
      setIsSaving(true);
      setError("");

      // API call
      const created = await addCollection(name);
      // Expect created to be the new collection object {id, name, quantity...}
      // If your API returns something else, tweak here.
      const newItem = new Collection(created['collection_type'], created['id']);

      // insert at top (or bottom)
      setCollections((prev) => [newItem, ...prev]);

      resetAdd();
    } catch (e) {
      console.error(e);
      setError("Couldn’t create collection. Try again.");
      setIsSaving(false);
    }
  };

  return (
    <div id="collection">
      {collections.length > 0 &&
        collections.map((c) => (
          <div className="collection-banner" key={c.id}>
            <CollectionDetailsComponent collection={c}/>
          </div>
        ))}

      {/* add collection banner */}
      <div className="collection-banner" style={{ cursor: "pointer" }}>
        {!adding ? (
          <div
            className="collection-add-row"
            onClick={() => {
              setAdding(true);
              setError("");
              setCollectionName("");
            }}
          >
            <p style={{ margin: 0 }}>Add collection</p>

            <div className="collection-button-small">
              <div className="collection-add">+</div>
            </div>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="collection-form">
            <label style={{ display: "block", marginBottom: 8 }}>
              Collection name
            </label>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                placeholder="e.g. Binder 1, 151 Master Set…"
                autoFocus
                disabled={isSaving}
                style={{ flex: 1 }}
              />

              <button type="submit" disabled={isSaving}>
                {isSaving ? "Adding…" : "Add"}
              </button>

              <button type="button" onClick={resetAdd} disabled={isSaving}>
                Cancel
              </button>
            </div>

            {error && (
              <div style={{ marginTop: 8, opacity: 0.9 }}>
                {error}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

const CollectionComponent = () => {
    const {user} = useAuth();
    return <div className='collection-page'>
        <PortfolioComponent user={user}/>
        {/* <CardCollectionComponent/> */}
    </div>
}

export default CollectionComponent